import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SubTitle from '../common/SubTitle';
import { Box, Paper, Button, CircularProgress, Snackbar, Alert } from '@mui/material';
import DriverDetailsSection from './DriverDetailsSection';
import AddressSection from './AddressSection';
import DocumentUploadSection from './DocumentUploadSection';
import config from '../../config.json';
import { useNavigate } from 'react-router';
import { useLocation, useSearchParams } from 'react-router-dom';

export default function DriversAddMain() {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams();
    const driverId = searchParams.get('id');

    // Define the input field styles that will be passed to child components
    const inputStyles = {
        height: 'auto',
        '& .MuiOutlinedInput-root': {
            height: 'auto',
            borderRadius: '16px',
            border: '2px solid #183059',
            '& fieldset': {
                border: 'none'
            }
        },
        '& .MuiInputBase-input': {
            padding: '16px 24px'
        },
        '& .Mui-error': {
            borderColor: '#d32f2f'
        }
    };

    // Style for red asterisk
    const redAsteriskStyle = {
        color: '#d32f2f',
        marginLeft: '2px',
    };

    // Get organizationId from localStorage
    const [organizationId, setOrganizationId] = useState();

    useEffect(() => {
        // Get organizationId from localStorage when component mounts
        const storedData = JSON.parse(localStorage.getItem('userState'));
        const storedOrgId = storedData?.user?.organizationId;
        if (storedOrgId) {
            setOrganizationId(parseInt(storedOrgId, 10));
        }
    }, []);

    console.log("organizationId", organizationId)
    const [formData, setFormData] = useState({
        // Driver Details - Updated to match the API structure
        driverId: '',
        organizationId: organizationId,
        firstName: '',
        lastName: '',
        email: '',
        phone_number: '',
        driving_license_number: '',
        dob: '',
        current_address: '',
        current_city: '',
        current_pincode: '',
        permanent_address: '',
        permanent_city: '',
        permanent_pincode: '',
        description: '',

        // UI-only fields (not sent to API)
        fullName: '',
        sameAsPresentAddress: false,
        isActive: true,
        hasSmartphone: false,
        preferredLanguage: '',

        // Documents
        documents: []
    });

    // Update organizationId when it changes in state
    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            organizationId
        }));
    }, [organizationId]);

    // API submission state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [showAlert, setShowAlert] = useState(false);

    // Validation state
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});


    const fetchDriverById = async (id) => {
        try {
            const response = await axios.get(`${config.baseURL}/driver/id`, {
                params: { id },
                withCredentials: true
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching driver:", error);
            setSubmitError("Failed to fetch driver data");
            setShowAlert(true);
            return null;
        }
    };
    useEffect(() => {
        const getDriverData = async () => {
            if (driverId) {
                setIsSubmitting(true);
                const driverData = await fetchDriverById(driverId);

                if (driverData) {
                    // Map API data to form data with consistent field naming
                    setFormData({
                        // API field names
                        driverId: driverData.driver_id || '',
                        organizationId: driverData.organization_id || organizationId,
                        firstName: driverData.first_name || '',
                        lastName: driverData.last_name || '',
                        email: driverData.email || '',
                        phone_number: driverData.phone_number || '',
                        driving_license_number: driverData.driving_license_number || '',
                        dob: driverData.dob ? new Date(driverData.dob).toISOString().split('T')[0] : '',
                        current_address: driverData.current_address || '',
                        current_city: driverData.current_city || '',
                        current_pincode: driverData.current_pincode || '',
                        permanent_address: driverData.permanent_address || '',
                        permanent_city: driverData.permanent_city || '',
                        permanent_pincode: driverData.permanent_pincode || '',
                        description: driverData.description || '',

                        // UI-specific fields - properly mapped
                        fullName: `${driverData.first_name || ''} ${driverData.last_name || ''}`.trim(),
                        phoneNumber: driverData.phone_number || '',
                        drivingLicenseId: driverData.driving_license_number || '',
                        dateOfBirth: driverData.dob ? new Date(driverData.dob).toISOString().split('T')[0] : '',

                        // UI field names for address
                        presentAddress: driverData.current_address || '',
                        presentCity: driverData.current_city || '',
                        presentPincode: driverData.current_pincode || '',
                        permanentAddress: driverData.permanent_address || '',
                        permanentCity: driverData.permanent_city || '',
                        permanentPincode: driverData.permanent_pincode || '',

                        // Check if addresses match
                        sameAsPresentAddress: driverData.current_address &&
                            driverData.permanent_address &&
                            driverData.current_address === driverData.permanent_address &&
                            driverData.current_city === driverData.permanent_city &&
                            driverData.current_pincode === driverData.permanent_pincode,

                        moreDetails: driverData.description || '',
                        isActive: true,
                        hasSmartphone: false,
                        preferredLanguage: '',
                        documents: driverData.documents || []
                    });
                }
                setIsSubmitting(false);
            }
        };

        getDriverData();
    }, [driverId, organizationId]);


    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prevState => {
            // Create the new state
            const newState = {
                ...prevState,
                [name]: type === 'checkbox' ? checked : value
            };

            // Handle full name splitting into first and last name
            if (name === 'fullName') {
                const nameParts = value.trim().split(' ');
                if (nameParts.length > 0) {
                    newState.firstName = nameParts[0];
                    newState.lastName = nameParts.slice(1).join(' ');
                }
            }

            // If "Same as Present Address" checkbox is checked, copy the present address values
            if (name === 'sameAsPresentAddress' && checked) {
                newState.permanentAddress = prevState.presentAddress;
                newState.permanentCity = prevState.presentCity;
                newState.permanentPincode = prevState.presentPincode;

                // Also update API field names
                newState.permanent_address = prevState.current_address;
                newState.permanent_city = prevState.current_city;
                newState.permanent_pincode = prevState.current_pincode;
            }

            // Bi-directional mapping between UI field names and API field names
            // UI to API mapping
            if (name === 'presentCity') {
                newState.current_city = value;
            } else if (name === 'presentAddress') {
                newState.current_address = value;
            } else if (name === 'presentPincode') {
                newState.current_pincode = value;
            } else if (name === 'permanentCity') {
                newState.permanent_city = value;
            } else if (name === 'permanentAddress') {
                newState.permanent_address = value;
            } else if (name === 'permanentPincode') {
                newState.permanent_pincode = value;
            } else if (name === 'dateOfBirth') {
                newState.dob = value;
            } else if (name === 'phoneNumber') {
                newState.phone_number = value;
            } else if (name === 'drivingLicenseId') {
                newState.driving_license_number = value;
            } else if (name === 'moreDetails') {
                newState.description = value;
            }

            // API to UI mapping (for completeness)
            if (name === 'current_city') {
                newState.presentCity = value;
            } else if (name === 'current_address') {
                newState.presentAddress = value;
            } else if (name === 'current_pincode') {
                newState.presentPincode = value;
            } else if (name === 'permanent_city') {
                newState.permanentCity = value;
            } else if (name === 'permanent_address') {
                newState.permanentAddress = value;
            } else if (name === 'permanent_pincode') {
                newState.permanentPincode = value;
            } else if (name === 'dob') {
                newState.dateOfBirth = value;
            } else if (name === 'phone_number') {
                newState.phoneNumber = value;
            } else if (name === 'driving_license_number') {
                newState.drivingLicenseId = value;
            } else if (name === 'description') {
                newState.moreDetails = value;
            }

            return newState;
        });

        // Mark field as touched
        setTouched(prev => ({
            ...prev,
            [name]: true
        }));

        // Validate the field
        validateField(name, type === 'checkbox' ? checked : value);
    };


    const validateField = (name, value) => {
        let newErrors = { ...errors };

        switch (name) {
            case 'fullName':
                if (!value.trim()) {
                    newErrors[name] = 'Full Name is required';
                } else {
                    delete newErrors[name];
                }
                break;
            case 'driverId':
                if (!value.trim()) {
                    newErrors[name] = 'Driver ID is required';
                } else {
                    delete newErrors[name];
                }
                break;
            case 'drivingLicenseId':
            case 'driving_license_number':
                if (!value.trim()) {
                    newErrors[name] = 'Driving License ID is required';
                } else {
                    delete newErrors[name];
                }
                break;
            case 'email':
                if (value.trim() && !/^\S+@\S+\.\S+$/.test(value)) {
                    newErrors[name] = 'Enter a valid email address';
                } else {
                    delete newErrors[name];
                }
                break;
            case 'phoneNumber':
            case 'phone_number':
                if (value.trim() && !/^[0-9]{10}$/.test(value)) {
                    newErrors[name] = 'Enter a valid 10-digit phone number';
                } else {
                    delete newErrors[name];
                }
                break;
            case 'presentCity':
            case 'current_city':
                if (!value.trim()) {
                    newErrors[name] = 'City is required';
                } else {
                    delete newErrors[name];
                }
                break;
            case 'permanentCity':
            case 'permanent_city':
                if (!formData.sameAsPresentAddress && !value.trim()) {
                    newErrors[name] = 'City is required';
                } else {
                    delete newErrors[name];
                }
                break;
            default:
                break;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateForm = () => {
        let isValid = true;
        let newTouched = { ...touched };
        let newErrors = { ...errors };

        // Required fields to validate
        const requiredFields = ['fullName', 'driverId', 'drivingLicenseId', 'presentCity'];

        // Add permanent city if "Same as Present Address" is not checked
        if (!formData.sameAsPresentAddress) {
            requiredFields.push('permanentCity');
        }

        // Validate all required fields
        for (const field of requiredFields) {
            newTouched[field] = true;
            const value = formData[field];

            if (!value || (typeof value === 'string' && !value.trim())) {
                newErrors[field] = `${field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1')} is required`;
                isValid = false;
            }
        }

        // Validate email if present
        if (formData.email && !/^\S+@\S+\.\S+$/.test(formData.email)) {
            newTouched.email = true;
            newErrors.email = 'Enter a valid email address';
            isValid = false;
        }

        // Validate phone number if present
        if (formData.phone_number && !/^[0-9]{10}$/.test(formData.phone_number)) {
            newTouched.phone_number = true;
            newErrors.phone_number = 'Enter a valid 10-digit phone number';
            isValid = false;
        }

        setTouched(newTouched);
        setErrors(newErrors);
        return isValid;
    };

    // Using the provided addDriver function for API submission
    const addDriverToAPI = async (driverData) => {
        try {
            const response = await axios.post(`${config.baseURL}/driver`, driverData, {
                withCredentials: true
            });
            return response.data;
        } catch (error) {
            console.log("Error in adding driver:", error);
            throw error;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (validateForm()) {
            setIsSubmitting(true);
            setSubmitError(null);

            try {
                // Prepare data in the format expected by the API
                const dataToSend = {
                    driver_id: formData.driverId,
                    organization_id: formData.organizationId,
                    first_name: formData.firstName,
                    last_name: formData.lastName,
                    email: formData.email,
                    phone_number: formData.phone_number,
                    driving_license_number: formData.driving_license_number,
                    dob: formData.dob ? formData.dob : null, // Keep date format as YYYY-MM-DD for backend parsing
                    current_address: formData.current_address,
                    current_city: formData.current_city,
                    current_pincode: formData.current_pincode,
                    permanent_address: formData.permanent_address,
                    permanent_city: formData.permanent_city,
                    permanent_pincode: formData.permanent_pincode,
                    description: formData.description
                };

                // If we have a driver ID, include it for update operation
                if (driverId) {
                    //update existing driver
                    await axios.put(`${config.baseURL}/driver/${driverId}`, dataToSend, {
                        withCredentials: true
                    });
                    console.log('Driver updated successfully');
                } else {
                    // Create new driver
                    await addDriverToAPI(dataToSend);
                    console.log('Driver created successfully');
                }

                setSubmitSuccess(true);
                setShowAlert(true);
                setTimeout(() => {
                    navigate('/driver');
                }, 1500); // Add a small delay before navigation so the user can see the success message

            } catch (error) {
                console.error('Submission error:', error.response?.data || error.message);
                setSubmitError(error.response?.data?.msg || error.message || 'Failed to submit driver data');
                setShowAlert(true);
            } finally {
                setIsSubmitting(false);
            }
        } else {
            console.log('Form has errors');
        }
    };

    const handleCancel = () => {
        console.log('Form cancelled');
        // Navigation or reset logic would go here
    };

    const handleImageUpload = (file) => {
        // Image upload logic would go here
        console.log('Image uploaded:', file);
    };

    // Function to add a document
    const handleAddDocument = (document) => {
        setFormData(prev => ({
            ...prev,
            documents: [...prev.documents, document]
        }));
    };

    // Helper functions for validation
    const hasError = (field) => touched[field] && Boolean(errors[field]);
    const getErrorMessage = (field) => touched[field] ? errors[field] : '';

    // Handle alert close
    const handleAlertClose = () => {
        setShowAlert(false);
    };

    return (
        <>
            <SubTitle title="Create Driver" path="Drivers > Create Driver" />
            <Box sx={{ margin: "0px 40px" }}>
                <Paper
                    sx={{
                        borderRadius: "8px",
                        overflow: "hidden",
                        padding: "20px",
                        border: "1px solid #A5DCF3",
                        boxShadow: "-2px 2px 5px 0px #00000040",
                    }}
                >
                    <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 1 }}>
                        {/* Driver Details Section Component */}
                        <DriverDetailsSection
                            formData={formData}
                            handleInputChange={handleInputChange}
                            handleImageUpload={handleImageUpload}
                            hasError={hasError}
                            getErrorMessage={getErrorMessage}
                            inputStyles={inputStyles}
                            redAsteriskStyle={redAsteriskStyle}
                        />

                        {/* Present Address Section Component - Updated prop names */}
                        <AddressSection
                            title="Present Address"
                            addressType="present"  // This will be mapped to current_* fields internally
                            formData={formData}
                            handleInputChange={handleInputChange}
                            hasError={hasError}
                            getErrorMessage={getErrorMessage}
                            inputStyles={inputStyles}
                            redAsteriskStyle={redAsteriskStyle}
                        />

                        {/* Permanent Address Section Component */}
                        <AddressSection
                            title="Permanent Address"
                            addressType="permanent"
                            formData={formData}
                            handleInputChange={handleInputChange}
                            hasError={hasError}
                            getErrorMessage={getErrorMessage}
                            inputStyles={inputStyles}
                            redAsteriskStyle={redAsteriskStyle}
                            showSameAsCheckbox={true}
                        />
                    </Box>
                </Paper>

                <Paper
                    sx={{
                        borderRadius: "8px",
                        overflow: "hidden",
                        padding: "20px",
                        border: "1px solid #A5DCF3",
                        boxShadow: "-2px 2px 5px 0px #00000040",
                        marginTop: 3
                    }}
                >
                    {/* Document Upload Section Component */}
                    <DocumentUploadSection
                        documents={formData.documents}
                        onAddDocument={handleAddDocument}
                    />

                    {/* Action Buttons */}
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 2,
                            marginTop: 4
                        }}
                    >
                        <Button
                            variant="outlined"
                            onClick={handleCancel}
                            disabled={isSubmitting}
                            sx={{
                                width: 122,
                                height: 55,
                                borderRadius: '8px',
                                borderWidth: '1px',
                                paddingTop: '16px',
                                paddingRight: '24px',
                                paddingBottom: '16px',
                                paddingLeft: '24px',
                                background: '#F2F2F3',
                                border: '1px solid #414958',
                                color: "#1F242E",
                                fontFamily: "Roboto",
                                fontWeight: "500",
                                fontSize: "20px",
                                textTransform: "none",
                                ":hover": {
                                    background: '#F2F2F3',
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            type="submit"
                            disabled={isSubmitting}
                            onClick={handleSubmit}
                            sx={{
                                width: 125,
                                height: 55,
                                borderRadius: '8px',
                                borderWidth: '1px',
                                paddingTop: '16px',
                                paddingRight: '24px',
                                paddingBottom: '16px',
                                paddingLeft: '24px',
                                background: '#F9E719',
                                border: '1px solid #414958',
                                color: "#1F242E",
                                fontFamily: "Roboto",
                                fontWeight: "500",
                                fontSize: "20px",
                                textTransform: "none",
                                ":hover": {
                                    background: '#F9E719',
                                }
                            }}
                        >
                            {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Submit'}
                        </Button>
                    </Box>
                </Paper>
                <Box sx={{ height: 9 }}></Box>
            </Box>

            {/* Success/Error Alerts */}
            <Snackbar
                open={showAlert}
                autoHideDuration={6000}
                onClose={handleAlertClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleAlertClose}
                    severity={submitSuccess ? "success" : "error"}
                    sx={{ width: '100%' }}
                >
                    {submitSuccess
                        ? "Driver data successfully submitted!"
                        : submitError || "An error occurred while submitting the form."}
                </Alert>
            </Snackbar>
        </>
    );
}